import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Avatar, useTheme } from '@mui/material';
import { Video } from 'lucide-mui';
import type { RootState } from '../../../../reducers';
import newLogo from '../../../../assets/icons/new_logo.svg';

const VideoThinking = () => {
  const theme = useTheme();
  const thumbUrl = useSelector(
    (state: RootState) => state.selectedVideo.file?.thumbUrl
  );

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '1rem',
      }}
    >
      <Avatar
        sx={{
          marginRight: '0.5rem',
          bgcolor: 'transparent',
          '& img': {
            height: '100%',
            width: '100%',
            maxHeight: 30,
            maxWidth: 120,
            objectFit: 'contain',
          },
        }}
        src={newLogo}
        alt="ScreenApp"
        variant="square"
      />
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          borderRadius: '12px',
          borderTopLeftRadius: '4px',
          position: 'relative',
          maxWidth: '70%',
          minWidth: '300px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%', // 16:9 aspect ratio
            backgroundImage: thumbUrl ? `url(${thumbUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.05)',
          }}
        >
          {!thumbUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Video
                style={{
                  color:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)',
                  width: '48px',
                  height: '48px',
                }}
              />
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(
                to bottom,
                ${theme.palette.background.paper}00,
                ${theme.palette.background.paper}66,
                ${theme.palette.background.paper}00
              )`,
              animation: 'scan 2s ease-in-out infinite',
              '@keyframes scan': {
                '0%': {
                  transform: 'translateY(-100%)',
                },
                '100%': {
                  transform: 'translateY(100%)',
                },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            padding: theme.spacing(1.5, 2),
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Box
            component="span"
            sx={{
              color: theme.palette.text.primary,
              fontSize: '0.9rem',
            }}
          >
            Analyzing, this may take a moment
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                component="span"
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  opacity: 0.7,
                  animation: 'fadeInOut 1.5s infinite',
                  animationDelay: `${index * 0.2}s`,
                  '@keyframes fadeInOut': {
                    '0%, 100%': { opacity: 0.3 },
                    '50%': { opacity: 0.7 },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoThinking;
